import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  HStack,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CherryPayApi } from "../../api/models";
import DateUtil from "../../util/DateUtil";
import {
  NotificationScheduleDateIcon,
  NotificationScheduleTimeIcon,
} from "../../styles/icons";
import { isNumber } from "lodash-es";
import { EligibleMembersDrawer } from "./EligibleMembersDrawer";
import { GeoLocationsDrawer } from "./GeoLocationsDrawer";
import { MembershipFilterSummary } from "../MemberFilterQueryModal/MemberFilterSummary";
import { isEmptyStr } from "../../util/StringUtil";

interface NotificationScheduleDetailsProps {
  notification: CherryPayApi.CherryPassNotificationScheduleDetail;
  isEditing?: boolean;
  memberCount?: number | null;
  businessId: string;
}

export const NotificationScheduleDetails = ({
  notification,
  isEditing = false,
  memberCount,
  businessId,
}: NotificationScheduleDetailsProps) => {
  const [isEligibleMembersDrawerOpen, setIsEligibleMembersDrawerOpen] =
    useState(false);
  const [isGeoLocationsDrawerOpen, setIsGeoLocationsDrawerOpen] =
    useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const locationOverrides =
    notification.LocationMessageOverride?.LocationMessageOverrides ?? {};
  const overriddenLocationCount = Object.values(locationOverrides).filter(
    (v) => !isEmptyStr(v)
  ).length;

  const hasSegment = !!notification.MemberFilterSegment?.SegmentId;
  const calculateDuration = () => {
    if (!notification.MaximumLocalScheduleSendDateTime) return null;
    if (!notification.LocalScheduleSendDateTime) return null;

    const start = new Date(notification.LocalScheduleSendDateTime);
    const end = new Date(notification.MaximumLocalScheduleSendDateTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMinutes = Math.ceil(diffMs / 60000);

    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}`;
    }

    const diffHours = diffMinutes / 60;
    if (Number.isInteger(diffHours)) {
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
    }

    const roundedHours = Math.round(diffHours * 10) / 10;
    if (Math.abs(diffHours - roundedHours) < Number.EPSILON) {
      return `${roundedHours} hours`;
    }

    const wholeHours = Math.floor(diffHours);
    const remainingMinutes = diffMinutes % 60;
    return `${wholeHours} hour${
      wholeHours !== 1 ? "s" : ""
    } ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
  };

  const stopSendingText = calculateDuration() || "Deliver Anytime";

  return (
    <VStack align="start" spacing={1} mt={4} pt={6}>
      {overriddenLocationCount > 0 && (
        <HStack alignItems={"flex-start"} pb={4}>
          <Text fontWeight="medium" minWidth={"200px"}>
            Geo-Locations:
          </Text>
          <VStack alignItems={"flex-start"} spacing={1}>
            <Text>
              {overriddenLocationCount} location{" "}
              {overriddenLocationCount === 1 ? "message" : "messages"}{" "}
              overridden
            </Text>
            <Link
              color="cherryUi.600"
              onClick={() => setIsGeoLocationsDrawerOpen(true)}
              cursor="pointer"
            >
              {`View Details >`}
            </Link>
          </VStack>
        </HStack>
      )}
      {(notification.NotificationScheduleStatus === "Draft" ||
        notification.NotificationScheduleStatus === "Scheduled") && (
        <HStack alignItems={"flex-start"} pb={4}>
          <Text fontWeight="medium" minWidth={"200px"}>
            Total Eligible Members:
          </Text>
          <VStack alignItems={"flex-start"} spacing={1}>
            <Text>
              {isNumber(memberCount) ? memberCount.toString() : "Loading..."}
            </Text>
            {isNumber(memberCount) && memberCount > 0 && (
              <Link
                color="cherryUi.600"
                onClick={() => setIsEligibleMembersDrawerOpen(true)}
                cursor="pointer"
              >
                {`View Eligible Members >`}
              </Link>
            )}
          </VStack>
        </HStack>
      )}
      <HStack alignItems={"flex-start"} pb={4}>
        <Text fontWeight="medium" minWidth={"200px"}>
          Notification Schedule:
        </Text>
        <VStack alignItems={"flex-start"}>
          <HStack>
            <NotificationScheduleDateIcon />
            <Text>
              {notification.LocalScheduleSendDateTime
                ? DateUtil.getFormattedDateWithWeekday(
                    notification.LocalScheduleSendDateTime
                  )
                : ""}
            </Text>
          </HStack>
          <HStack>
            <NotificationScheduleTimeIcon />
            <Text>
              {notification.LocalScheduleSendDateTime
                ? DateUtil.getFormattedTimeFromUtcToLocalTimezone(
                    notification.LocalScheduleSendDateTime
                  )
                : ""}
            </Text>
          </HStack>
          {isEditing && notification.LocalScheduleSendDateTime && (
            <HStack>
              <Text>
                Your push notification will be sent to selected members at the
                scheduled send date and time, delivering directly to their
                digital wallet.
              </Text>
            </HStack>
          )}
        </VStack>
      </HStack>
      {!isEditing && (
        <HStack pb={4} alignItems={"flex-start"}>
          <Text fontWeight="medium" minWidth={"200px"}>
            Member Filters:
          </Text>
          {hasSegment ? (
            <VStack alignItems={"flex-start"} spacing={1}>
              <Text fontWeight="semibold">
                {notification.MemberFilterSegment!.SegmentName}
              </Text>
              <Button
                variant="link"
                color="cherryButton.800"
                size="sm"
                fontWeight="normal"
                _hover={{ textDecoration: "none" }}
                sx={{
                  "&:hover > span:first-of-type": {
                    textDecoration: "underline",
                  },
                }}
                marginInlineStart={"0.3rem"}
                rightIcon={
                  <Box
                    as="span"
                    display="inline-block"
                    transform={
                      filtersExpanded ? "rotate(90deg)" : "rotate(0deg)"
                    }
                    transition="transform 0.2s ease"
                    mt={"2px"}
                  >
                    {">"}
                  </Box>
                }
                onClick={() => setFiltersExpanded((prev) => !prev)}
              >
                <Box as="span">
                  {filtersExpanded ? "Hide Filters" : "View Filters"}
                </Box>
              </Button>
              <Collapse in={filtersExpanded} animateOpacity>
                <MembershipFilterSummary
                  queryString={notification.MemberFilterQuery ?? ""}
                  variant="compact"
                />
              </Collapse>
            </VStack>
          ) : (
            <MembershipFilterSummary
              queryString={notification.MemberFilterQuery ?? ""}
              variant="compact"
            />
          )}
        </HStack>
      )}
      <HStack>
        <Text fontWeight="medium" minWidth={"200px"}>
          Stop Sending After:
        </Text>
        <NotificationScheduleTimeIcon />
        <Text>{stopSendingText}</Text>
      </HStack>
      {isEligibleMembersDrawerOpen && (
        <EligibleMembersDrawer
          isOpen={isEligibleMembersDrawerOpen}
          onClose={() => setIsEligibleMembersDrawerOpen(false)}
          businessId={businessId}
          memberFilterQuery={notification.MemberFilterQuery}
          totalMembers={isNumber(memberCount) ? memberCount.toString() : "0"}
        />
      )}
      {isGeoLocationsDrawerOpen && (
        <GeoLocationsDrawer
          isOpen={isGeoLocationsDrawerOpen}
          onClose={() => setIsGeoLocationsDrawerOpen(false)}
          businessId={businessId}
          overrides={locationOverrides}
        />
      )}
    </VStack>
  );
};
